import { useEffect, useRef, useState } from "react";
import type { CommunityMember } from "@/react/types/CommunityMember";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";

const getInitials = (firstName?: string, lastName?: string): string => {
  if (!firstName && !lastName) return "";

  const initials = [];
  if (firstName) initials.push([...firstName][0]);
  if (lastName) initials.push([...lastName][0]);

  return initials.join("").toUpperCase();
};

interface CommunityPreviewData {
  achieveWithCircle?: string;
  avatar_url?: string;
  buildingBlocks: string[];
  customerJourneyStage?: string;
  displayName: string;
  email?: string;
  fullName: string;
  nameToUse: string;
  shouldAnimateAchieveWithCircle: boolean;
  shouldAnimateCustomerJourneyStage: boolean;
  slug?: string;
  slugToUse?: string;
  userInitials: string;
}

export const useCommunityPreviewData = (
  communityName: string,
  customBuildingBlocks?: string[],
  animateField?: "customerJourneyStage" | "achieveWithCircle",
  watchedSlug?: string,
  watchedName?: string,
): CommunityPreviewData & { hasUserModifiedName: boolean } => {
  const context = useCurrentCommunityContext() as {
    currentUser: CommunityMember;
    currentCommunity: CurrentCommunity;
  };
  const { currentUser, currentCommunity } = context;

  const blocks = customBuildingBlocks ?? [];

  const firstName = currentUser?.first_name ?? "";
  const lastName = currentUser?.last_name ?? "";
  const userInitials = getInitials(firstName, lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  const customerJourneyStage =
    currentCommunity?.sign_up_flow_responses?.customer_journey_stage;
  const achieveWithCircle =
    currentCommunity?.sign_up_flow_responses?.achieve_with_circle;

  const displayName = communityName || currentCommunity?.name || "";
  const slug = currentCommunity?.slug;

  const slugToUse = watchedSlug || slug;
  const nameToUse = watchedName || displayName;

  const shouldAnimateCustomerJourneyStage =
    animateField === "customerJourneyStage";
  const shouldAnimateAchieveWithCircle = animateField === "achieveWithCircle";

  const [hasUserModifiedName, setHasUserModifiedName] = useState(false);
  const initialNameRef = useRef(watchedName);

  useEffect(() => {
    if (
      !hasUserModifiedName &&
      watchedName !== undefined &&
      watchedName !== initialNameRef.current
    ) {
      setHasUserModifiedName(true);
    }
  }, [watchedName, hasUserModifiedName]);

  return {
    displayName,
    customerJourneyStage,
    achieveWithCircle,
    buildingBlocks: blocks,
    userInitials,
    fullName,
    email: currentUser?.email,
    avatar_url: currentUser?.avatar_url,
    shouldAnimateCustomerJourneyStage,
    shouldAnimateAchieveWithCircle,
    slug,
    slugToUse,
    nameToUse,
    hasUserModifiedName,
  };
};
